/**
 * HOOK: usePaymentCase
 * Fetch des payment_cases visibles pour un contact (solo + duo via crew_members)
 * enrichis des relations products(name), payer, payment_attempts.
 *
 * Pattern identique à useCrewsForContact (hook relationnel léger).
 *
 * Depuis review finding B (2026-04-19) : utilise la RPC
 * `list_payment_cases_for_contact` (migration 00067) pour remonter aussi les
 * dossiers duo ou le contact n'est pas contact_id mais co-equipier via
 * crew_members. L'enrichissement (product, payer, payment_attempts) est fait
 * en une seconde requete par IDs, ce qui preserve la forme
 * PaymentCaseWithRelations attendue par les consommateurs.
 */

import { useEffect, useState, useCallback } from 'react';
import { useSupabase } from './useSupabase';
import type { PaymentCase, PaymentAttempt } from '../types/payment';

// Tokens BED rattachés au dossier (embed one-to-many). Sert au ré-affichage du
// lien de paiement actif sur la carte d'un dossier BED (PaymentSection).
export interface BedTokenRow {
  token: string;
  created_at: string;
  expires_at: string;
  used_at: string | null;
}

export interface PaymentCaseWithRelations extends PaymentCase {
  product_name: string | null;
  // Code produit réel (products.code) : route le tunnel de paiement BED PAR
  // PRODUIT au ré-affichage du lien (review Victor #3, cf. buildBedPaymentLink).
  product_code: string | null;
  payer_name: string | null;
  payment_attempts: PaymentAttempt[];
  bed_tokens: BedTokenRow[];
}

interface UsePaymentCaseReturn {
  paymentCases: PaymentCaseWithRelations[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function usePaymentCase(contactId: string | undefined): UsePaymentCaseReturn {
  const supabase = useSupabase();
  const [paymentCases, setPaymentCases] = useState<PaymentCaseWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPaymentCases = useCallback(async () => {
    if (!contactId) {
      setPaymentCases([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 1. RPC : liste des payment_cases visibles par ce contact (solo + duo).
      //    Retourne des lignes payment_cases brutes (pas de jointures).
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'list_payment_cases_for_contact',
        { p_contact_id: contactId }
      );

      if (rpcError) throw rpcError;

      const baseCases = (rpcData ?? []) as PaymentCase[];
      // BED-ENTRY : on ne filtre plus sur scope='bpi'. Les dossiers BED
      // (scope='bed') doivent aussi remonter pour afficher le « reste à payer »
      // et l'historique multi-CB. On enrichit TOUS les cases.
      const caseIds = baseCases.map((pc) => pc.id);

      if (caseIds.length === 0) {
        setPaymentCases([]);
        return;
      }

      // 2. Enrichissement : product / payer / payment_attempts en une requete.
      const { data: enrichedData, error: enrichError } = await supabase
        .from('payment_cases')
        .select(
          `
          id,
          product:products(name, code),
          payer:contacts!payment_cases_payer_contact_id_fkey(first_name, last_name),
          payment_attempts(*),
          bed_tokens(token, created_at, expires_at, used_at)
        `
        )
        .in('id', caseIds);

      if (enrichError) throw enrichError;

      type EnrichedRow = {
        id: string;
        product?: { name: string | null; code: string | null } | null;
        payer?: { first_name: string | null; last_name: string | null } | null;
        payment_attempts?: PaymentAttempt[] | null;
        bed_tokens?: BedTokenRow[] | null;
      };

      const enrichedById = new Map<string, EnrichedRow>(
        ((enrichedData ?? []) as unknown as EnrichedRow[]).map((row) => [row.id, row])
      );

      // Tri descendant par created_at (la RPC trie deja, mais on garde la
      // coherence apres l'enrichissement). BPI et BED confondus.
      const transformed: PaymentCaseWithRelations[] = baseCases
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
        .map((pc) => {
          const extra = enrichedById.get(pc.id);
          const payer = extra?.payer ?? null;
          return {
            ...pc,
            product_name: extra?.product?.name ?? null,
            product_code: extra?.product?.code ?? null,
            payer_name: payer
              ? `${payer.first_name ?? ''} ${payer.last_name ?? ''}`.trim() || null
              : null,
            payment_attempts: extra?.payment_attempts ?? [],
            bed_tokens: extra?.bed_tokens ?? [],
          };
        });

      setPaymentCases(transformed);
    } catch (err) {
      console.error('[usePaymentCase] Error:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des paiements');
      setPaymentCases([]);
    } finally {
      setLoading(false);
    }
  }, [contactId, supabase]);

  useEffect(() => {
    fetchPaymentCases();
  }, [fetchPaymentCases]);

  return {
    paymentCases,
    loading,
    error,
    refresh: fetchPaymentCases,
  };
}

// Variante crew : select direct par crew_id. La RLS payment_cases_auth_all
// autorise le SELECT a tout authenticated, donc pas besoin de RPC dediee.
// Forme de retour identique a usePaymentCase pour reutiliser PaymentCaseCard.
export function usePaymentCasesByCrew(crewId: string | undefined): UsePaymentCaseReturn {
  const supabase = useSupabase();
  const [paymentCases, setPaymentCases] = useState<PaymentCaseWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPaymentCases = useCallback(async () => {
    if (!crewId) {
      setPaymentCases([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .from('payment_cases')
        .select(
          `
          *,
          product:products(name, code),
          payer:contacts!payment_cases_payer_contact_id_fkey(first_name, last_name),
          payment_attempts(*),
          bed_tokens(token, created_at, expires_at, used_at)
        `
        )
        .eq('crew_id', crewId)
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      type Row = PaymentCase & {
        product?: { name: string | null; code: string | null } | null;
        payer?: { first_name: string | null; last_name: string | null } | null;
        payment_attempts?: PaymentAttempt[] | null;
        bed_tokens?: BedTokenRow[] | null;
      };

      const transformed: PaymentCaseWithRelations[] = ((data ?? []) as unknown as Row[]).map(
        (row) => {
          const { product, payer, payment_attempts, bed_tokens, ...pc } = row;
          return {
            ...(pc as PaymentCase),
            product_name: product?.name ?? null,
            product_code: product?.code ?? null,
            payer_name: payer
              ? `${payer.first_name ?? ''} ${payer.last_name ?? ''}`.trim() || null
              : null,
            payment_attempts: payment_attempts ?? [],
            bed_tokens: bed_tokens ?? [],
          };
        }
      );

      setPaymentCases(transformed);
    } catch (err) {
      console.error('[usePaymentCasesByCrew] Error:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des paiements');
      setPaymentCases([]);
    } finally {
      setLoading(false);
    }
  }, [crewId, supabase]);

  useEffect(() => {
    fetchPaymentCases();
  }, [fetchPaymentCases]);

  return {
    paymentCases,
    loading,
    error,
    refresh: fetchPaymentCases,
  };
}
